import { STEP_TEXT } from './stepText';

// Helper to safely retrieve the HTML block (or array of blocks for legacy
// entries) for a given set of parameters.  Walks the STEP_TEXT hierarchy,
// applying `default` fall-backs at each level.  Returns `undefined` if no
// match is found.

/**
 * @param {Object} opts
 * @param {string} opts.ttGroup   Sponsor org, e.g. "LF", "VA"
 * @param {string|number} opts.ehrVendor  Vendor code – can be falsy
 * @param {string} opts.testType  e.g. "inpatient", "ambulatory"
 * @param {string} opts.step      Logical identifier, e.g. "step2"
 * @param {boolean} opts.timeoutsOn  Whether to return the variant that
 *                                   includes timeout language.
 * @returns {string|string|undefined}  HTML string (or array) if found.
 */
export function getStepText({ ttGroup, ehrVendor, testType, step, timeoutsOn }) {
  const orgBlock = STEP_TEXT[ttGroup] || STEP_TEXT.default;

  const vendorKey = ehrVendor != null ? String(ehrVendor) : 'default';

  // DEBUG: Surface copy-selection parameters so we can diagnose sponsor/vendor issues
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[copyHelpers#getStepText]', {
      sponsorOrg: ttGroup,
      ehrVendorRaw: ehrVendor,
      vendorKey,
      testType,
      timeoutsOn,
      step
    });
  }

  const vendorBlock = orgBlock[vendorKey] || orgBlock.default || STEP_TEXT.default;

  // If explicit testType block not found, fall back to vendorBlock.default, or
  // the first entry inside vendorBlock (helps when only one testType is
  // defined, e.g., `inpatient`).
  const typeKey = (testType || 'default').toLowerCase();
  const typeBlock =
    vendorBlock[typeKey] ||
    vendorBlock.default ||
    Object.values(vendorBlock)[0] ||
    {};

  // Try to fetch requested step.  If not found, fall back to nested `default` block (if present)
  const stepBlock =
    typeBlock[step] ||
    (typeBlock.default && typeBlock.default[step]) ||
    {};

  const variantKey = timeoutsOn ? 'timeoutsOn' : 'timeoutsOff';
  const selectedText = stepBlock[variantKey];

  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[copyHelpers#getStepText] selected text', selectedText);
  }

  return selectedText;
}

// Convenience wrapper to fetch bullet list and substitute placeholders.
export function getStepTextWithVars(params, vars = {}) {
  const raw = getStepText(params);
  if (raw === undefined) return [];

  // Normalise to an array so callers can `map` without special-casing.
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map(str =>
    Object.entries(vars).reduce(
      (acc, [key, val]) => acc.replaceAll(`{${key}}`, val),
      str
    )
  );
}
